import _extends from "@babel/runtime/helpers/extends";
import React, { useCallback, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { animated, useTransition } from '@react-spring/web';
import Notification from './Notification';
import { PAGE_CONTAINER_WIDTH } from '../constants';
import { zIndexValue as zIndexMap } from '../helpers/position';
import { NOTIFICATION_CLOSE_DURATION, NOTIFICATION_MARGIN } from './constants';
import { EMPTY_ARRAY } from '../helpers/types';
import { styleArrayForBreakpoints } from '../helpers/media-style';
import { useCombineRefs, useDeviceType, useDidUpdateEffect, useHoverEvents, useMeasure, useMouseHover } from '../hooks';
import { width } from '../style/width';
import { right } from '../style/right';
import { bottom } from '../style/bottom';
import { top } from '../style/top';
import { zIndex as zIndexStyle } from '../style/z-index';
import { paddingBottom } from '../style/padding-bottom';
import { paddingTop } from '../style/padding-top';
import { paddingHorizontal } from '../style/padding-horizontal';
const Container = styled.div`
  display: flex;
  flex-direction: column;
  pointer-events: none;

  ${({
  $zIndex
}) => zIndexStyle($zIndex)}
  ${({
  $fixed,
  $absolute
}) => styleArrayForBreakpoints([$fixed, $absolute], ([$fixed, $absolute]) => `position: ${$fixed ? 'fixed' : $absolute ? 'absolute' : 'static'};`)}
  
  ${({
  $position
}) => $position === 'box' && `
    position: static !important;
    z-index: unset !important;
  `}
  ${({
  $position,
  $top = 0,
  $right = 0
}) => $position === 'top-right' && css`
      width: 100%;
      align-items: flex-end;
      ${top($top)}
      ${right($right)}
    `}
  ${({
  $position,
  $width,
  $top = 0
}) => $position === 'top-center' && css`
      align-items: center;
      left: 50%;
      transform: translateX(-50%);
      ${top($top)}
      ${width($width)}
    `}
  ${({
  $position,
  $width,
  $bottom = 0
}) => $position === 'bottom-center' && css`
      align-items: center;
      bottom: 0;
      left: 50%;
      transform: translateX(-50%);
      ${bottom($bottom)}
      ${width($width)}
    `}
`;
const ScrollWrapper = styled.div`
  pointer-events: all;

  box-sizing: content-box;

  ${({
  $position,
  $mode
}) => $position === 'box' ? `
    max-height: none;
    overflow-y: visible;
  ` : `
    max-height: 100vh;
    overflow-y: ${$mode};
    ${$mode === 'scroll' && 'overflow-x: hidden;'}
  `}
`;
const ElementWrapper = styled.div`
  display: flex;
  box-sizing: border-box;
`;
const NotificationScrollInnerWrapper = styled.div`
  pointer-events: all;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  ${({
  $position
}) => $position !== 'bottom-center' && paddingTop(NOTIFICATION_MARGIN)}
  ${({
  $position
}) => $position === 'bottom-center' && paddingBottom(NOTIFICATION_MARGIN)}
  ${paddingHorizontal(NOTIFICATION_MARGIN)}
`;
const getInitialTranslate = (position = 'top-center') => {
  switch (position) {
    case 'bottom-center':
      return 'translate(0, 24px)';
    case 'top-center':
    default:
      return 'translate(0, -24px)';
    case 'box':
    case 'top-right':
      return 'translate(128px, 0)';
  }
};
const NotificationScrollBox = /*#__PURE__*/React.forwardRef(({
  position = 'top-center',
  children,
  ...props
}, forwardedRef) => {
  const [mode, setMode] = useState('visible');
  const {
    setRef: setMeasureRef,
    bounds
  } = useMeasure();
  useDidUpdateEffect(() => {
    if (bounds.height >= window.innerHeight) {
      if (mode !== 'scroll') {
        setMode('scroll');
      }
    } else {
      if (mode !== 'visible') {
        setMode('visible');
      }
    }
  }, [bounds]);
  return /*#__PURE__*/React.createElement(ScrollWrapper, _extends({
    $position: position,
    ref: useCombineRefs(forwardedRef, setMeasureRef),
    $mode: mode
  }, props), children);
});
const SPRING_CONFIG_PROPS = {
  tension: 125,
  friction: 20,
  precision: 0.1
};

/**
 * NotificationHub component.
 *
 * Render stacked notifications. With transitions for mount and unmount.
 */
const NotificationHub = ({
  duration = NOTIFICATION_CLOSE_DURATION,
  position = 'top-center',
  variant = 'info',
  refs,
  notifications = EMPTY_ARRAY,
  onCloseNotification,
  onHover,
  onLeave,
  component: NotificationComponent = Notification,
  config = SPRING_CONFIG_PROPS,
  trail = 0,
  fixed = true,
  absolute = false,
  zIndex: zIndexProp = zIndexMap('notification'),
  width = PAGE_CONTAINER_WIDTH,
  top = 0,
  bottom = 0,
  right = 0,
  ...otherProps
}) => {
  const {
    hasTouchScreen
  } = useDeviceType();
  const elements = useMemo(() => notifications.map((n, index) => {
    const onClose = n.closable === false ? null : () => onCloseNotification?.(n.key, variant, true);
    const onAutoClose = () => onCloseNotification?.(n.key, variant, false);
    return {
      entered: n.entered || false,
      index: index,
      key: n.key,
      component: /*#__PURE__*/React.createElement(NotificationComponent, _extends({}, n, {
        onAutoClose: onAutoClose,
        onClose: onClose
      }, position === 'top-center' ? {
        position: 'top'
      } : {}, position === 'bottom-center' ? {
        position: 'bottom'
      } : {}, position === 'box' ? {
        delay: null,
        width: '100%'
      } : {}))
    };
  }), [notifications, onCloseNotification, variant, position]);
  // @ts-ignore
  const transitions = useTransition(elements, position === 'box' ? {
    key: item => item.key,
    from: item => async next => {
      await next({
        opacity: 0,
        height: refs.current[item.key]?.offsetHeight || 'auto',
        transform: getInitialTranslate(position)
      });
    },
    enter: item => async next => {
      await next({
        opacity: 0,
        height: refs.current[item.key]?.offsetHeight || 'auto',
        transform: getInitialTranslate(position)
      });
    },
    update: item => async next => {
      if (!item.entered) {
        await next({
          opacity: 0,
          height: refs.current[item.key]?.offsetHeight || 'auto',
          transform: getInitialTranslate(position)
        });
      } else {
        await next({
          opacity: 1,
          height: refs.current[item.key]?.offsetHeight || 'auto',
          transform: 'translate(0, 0)'
        });
      }
    },
    leave: () => async next => {
      await next({
        opacity: 0
      });
      await next({
        height: 0
      });
    },
    config,
    trail
  } : {
    key: item => item.key,
    from: () => ({
      opacity: 0,
      height: 0,
      transform: getInitialTranslate(position)
    }),
    enter: item => async next => {
      await next({
        opacity: 1,
        height: refs.current[item.key]?.offsetHeight || 'auto',
        transform: 'translate(0, 0)'
      });
    },
    leave: () => async next => {
      await next({
        opacity: 0
      });
      await next({
        height: 0
      });
    },
    config,
    trail
  });
  const [hovered, setHovered] = useState(false);
  const [hoveredDebounced, setHoveredDebounced] = useState(false);

  // notify about hover/leave
  useDidUpdateEffect(() => {
    if (hoveredDebounced) {
      onHover?.();
    } else {
      onLeave?.();
    }
  }, [hoveredDebounced]);
  const timeoutHoverId = useRef(null);
  useDidUpdateEffect(() => {
    clearTimeout(timeoutHoverId.current);
    if (hovered) {
      setHoveredDebounced(true);
    } else {
      timeoutHoverId.current = setTimeout(() => setHoveredDebounced(false), duration);
      return () => clearTimeout(timeoutHoverId.current);
    }
  }, [hovered]);
  const handleMouseOver = useCallback(() => setHovered(true), []);
  const handleMouseOut = useCallback(() => setHovered(false), []);
  const {
    ref: hoverRef,
    onMouseOver,
    onMouseOut
  } = useHoverEvents(handleMouseOver, handleMouseOut);
  const {
    setRef: setHoverRef
  } = useMouseHover(onMouseOver, onMouseOut, hoverRef, hasTouchScreen);
  return /*#__PURE__*/React.createElement(Container, _extends({
    $top: top,
    $bottom: bottom,
    $right: right,
    $width: width,
    $zIndex: zIndexProp,
    $fixed: fixed,
    $absolute: absolute,
    $position: position
  }, otherProps), /*#__PURE__*/React.createElement(NotificationScrollBox, {
    position: position,
    ref: setHoverRef
  }, /*#__PURE__*/React.createElement(NotificationScrollInnerWrapper, {
    $position: position
  }, transitions((style, item, state, key) => /*#__PURE__*/React.createElement(animated.div, {
    key: `NotificationHub-${variant}-${key}`,
    style: {
      ...style,
      willChange: 'opacity, height, transform, z-index',
      overflow: 'visible',
      //@ts-ignore
      pointerEvents: style.opacity ? style.opacity.to(o => o < 1 ? 'none' : 'auto') : 'auto',
      ...(state === 'leave' ? {
        zIndex: 2
      } : {
        zIndex: 3
      }),
      ...(position === 'box' ? {
        width: '100%'
      } : {})
    }
  }, /*#__PURE__*/React.createElement(ElementWrapper, {
    ref: ref => {
      if (ref && !refs.current[item.key]) {
        refs.current[item.key] = ref;
      }
    }
  }, item.component))))));
};
export default NotificationHub;